import type { ReactNode } from 'react';

/**
 * Pack B layout primitive. A white card: rounded-2xl, single soft shadow, an
 * optional section-header row, and a padded body. Field rows use label-left /
 * value-right on desktop and stack on mobile (see {@link FieldRow}).
 */
export function FormSection({
  title,
  description,
  children,
  className = ''
}: {
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`bg-surface rounded-2xl shadow-sm ring-1 ring-slate-200/70 ${className}`}>
      {title && (
        <header className="px-5 sm:px-6 pt-4 pb-3 border-b border-slate-100">
          <h2 className="font-display text-base font-semibold text-ink">{title}</h2>
          {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
        </header>
      )}
      <div className="p-5 sm:p-6 space-y-4">{children}</div>
    </section>
  );
}

/**
 * A labelled field row: label to the left of the control on desktop, stacked on
 * mobile. Renders an optional hint (when no error) or an inline error message.
 */
export function FieldRow({
  label,
  htmlFor,
  hint,
  error,
  children
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-[minmax(0,11rem)_1fr] sm:items-start sm:gap-4">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink sm:pt-2">
        {label}
      </label>
      <div className="min-w-0">
        {children}
        {hint && !error && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        {error && <p className="text-xs text-danger mt-1">{error}</p>}
      </div>
    </div>
  );
}

/** Shared control styling (inputs/selects/textarea) — tokenized, pine focus. */
export const controlClass =
  'w-full rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm text-ink ' +
  'min-h-[44px] placeholder:text-slate-400 focus:outline-none focus:ring-2 ' +
  'focus:ring-pine focus:border-pine disabled:bg-slate-50 disabled:text-slate-400';
